<template>
  <section class="limits-modal w-full max-w-5xl relative" data-testid="limits-modal">
    <!-- Close Button (always top-right of modal) -->
    <div class="absolute top-4 right-4 z-10">
      <button class="close-button" @click="handleClose" data-testid="limits-modal-close">
        <Icon name="general.cross" size="base" />
      </button>
    </div>

    <!-- Two Column Layout -->
    <div class="flex flex-col md:flex-row">
      <!-- Left Column: Usage Stats -->
      <div class="flex-1 border-r border-zinc-200 dark:border-zinc-700">
        <LimitsUsageView :usage="usage" :plan="plan" />
      </div>

      <!-- Right Column: Upgrade Features -->
      <div class="flex-1">
        <div class="pt-10">
          <LimitsUpgradeView
            :upgrade-features="upgradeFeatures"
            :plan="plan"
            :hide-close="true"
            @explore-plans="handleExplorePlans"
            @talk-to-sam="handleTalkToSam"
            @close="handleClose"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { Icon } from '@/storybook/icon';
import LimitsUsageView from './LimitsUsageView.vue';
import LimitsUpgradeView from './LimitsUpgradeView.vue';
import type { LimitUsage, UpgradeFeature } from './types';
import type { PlanTier } from './planFeatures';
import { getFeaturesForPlan } from './planFeatures';
import infinityIcon from './infinity-icon.svg';
import integrationsIcon from './integrations-icon.svg';
import qualityIcon from './quality-icon.svg';
import adminIcon from './admin_tools.svg';
import aiIcon from './ai-icon.svg';
import requestLinksIcon from './request_links.svg';
import teamCollaborationIcon from './team_collaboration.svg';
import collectionsIcon from './collections.svg';
import scimIcon from './scim.svg';
import complianceIcon from './compliance.svg';
import unlimitedRecordingsIcon from './unlimited_recordings.svg';

// Icon mapping for plan features
const FEATURE_ICON_MAP: Record<string, string> = {
  'unlimited-recordings': unlimitedRecordingsIcon,
  integrations: integrationsIcon,
  'more-integrations': integrationsIcon,
  'premium-integrations': integrationsIcon,
  'quality-4k': qualityIcon,
  'admin-tools': adminIcon,
  'ai-smart-actions': aiIcon,
  'unlimited-smart-actions': aiIcon,
  'request-links': requestLinksIcon,
  'more-request-links': requestLinksIcon,
  'team-collaboration': teamCollaborationIcon,
  collections: collectionsIcon,
  'sso-scim-saml': scimIcon,
  'domain-control': adminIcon,
  'greater-compliance': complianceIcon,
  unlock: infinityIcon,
};

interface Props {
  usage?: LimitUsage[];
  plan?: PlanTier; // 'free' | 'create' | 'collaborate' | 'enterprise'
  onClose?: () => void;
  onExplorePlans?: () => void;
  onTalkToSam?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  usage: () => [
    { label: 'Uploads', current: 21, max: 25 },
    { label: 'Request Replies', current: 6, max: 10 },
    { label: 'AI Smart Actions', current: 2, max: 5 },
  ],
  plan: 'free',
});

// Get upgrade features based on plan tier
const upgradeFeatures = computed((): UpgradeFeature[] => {
  const planFeatures = getFeaturesForPlan(props.plan);

  // Map PlanFeature to UpgradeFeature format
  return planFeatures.map(feature => ({
    label: feature.text,
    icon: FEATURE_ICON_MAP[feature.icon] || infinityIcon,
  }));
});

const handleClose = () => {
  if (props.onClose) {
    props.onClose();
  }
};

const handleExplorePlans = () => {
  if (props.onExplorePlans) {
    props.onExplorePlans();
  } else {
    // Default behavior: navigate to pricing page
    window.location.href = '/plans?utm_source=web&utm_campaign=limit-modal';
  }
};

const handleTalkToSam = () => {
  if (props.onTalkToSam) {
    props.onTalkToSam();
  } else {
    window.open('https://zight.com/contact-sales?utm_source=share.zight.com-upgrade-modal&utm_medium=web', '_blank', 'noopener,noreferrer');
  }
};
</script>

<style scoped>
.limits-modal {
  @apply max-h-[90vh] overflow-auto;
}

.close-button {
  @apply w-6 h-6 flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer;
}
</style>
